import { Button, Flex, Stack, Text } from '@eleveo/components-react';
import { CheckmarkIcon, CrossIcon, DuplicateIcon } from '@eleveo/icons-react';
import { eachDayOfInterval, endOfWeek, startOfWeek } from 'date-fns';
import { FormApi } from 'final-form';
import { clone } from 'ramda';
import React, { useEffect, useState } from 'react';
import { useField } from 'react-final-form';
import { useTranslation } from 'react-i18next';

import { WeekDayTile } from './WeekDayTile';
import { getInitialValues } from './config';
import {
  SHIFT_EVEN_ODD_WEEK_CANCEL_SELECTION_BUTTON,
  SHIFT_EVEN_ODD_WEEK_CLEAR_BUTTON,
  SHIFT_EVEN_ODD_WEEK_CONFIRM_SELECTION_BUTTON,
  SHIFT_EVEN_ODD_WEEK_DAY_BUTTON,
  SHIFT_EVEN_ODD_WEEK_DUPLICATE_BUTTON,
  SHIFT_EVEN_WEEK_SWITCHER,
} from './constants';
import { DayTemplatePartial, ShiftTemplatesFormValues } from './types';
import { duplicateTemplateSettings, isDayTemplateEmpty, validateDayTemplates } from './utils';
import { useAuth } from '../../app/AuthorizationProvider';

export type Props = {
  currentDayTemplateIndex: number;
  form: FormApi<ShiftTemplatesFormValues, Partial<ShiftTemplatesFormValues>>;
  changeCurrentDayTemplateIndex: (newDayTemplateIndex: number) => void;
};

type DayConfig = {
  empty: boolean;
  selectedForDuplication: boolean;
  valid: boolean;
};

const weeksConfig = [
  {
    offset: 0,
    name: 'date.evenWeek',
  },
  {
    offset: 7,
    name: 'date.oddWeek',
  },
];

export const EvenOddWeekSwitcher: React.FC<Props> = ({
  currentDayTemplateIndex, // values in range [0, 13] where 0 - monday in even week, and 13 - sunday in odd week
  form,
  changeCurrentDayTemplateIndex,
}) => {
  const { localeObj } = useAuth();

  const { t } = useTranslation();
  const {
    input: { value: dayTemplates },
    meta: { error: dayTemplatesErrors },
  } = useField<DayTemplatePartial[]>('dayTemplates', { subscription: { error: true, value: true } });
  const [isLoaded, setLoaded] = useState(false);
  const [valuesInitialized, setValuesInitialized] = useState(false);

  const [isDuplicationActive, setDuplicationActive] = useState(false);
  const [daysConfig, setDaysConfig] = useState<Record<string, DayConfig>>(
    [...Array(14)].reduce(
      (config, _, index) => ({
        ...config,
        [index + 1]: {
          valid: false,
          selectedForDuplication: false,
          empty: isDayTemplateEmpty(dayTemplates[index]),
        },
      }),
      {}
    )
  );

  useEffect(() => {
    setDaysConfig((config) => {
      /**
       * Update validity of current day settings whenever form detects some error
       * - do note that form handles errors correctly only for rendered fields, so it doesn't really
       * know if there is some error on currently not displayed days' fields
       */
      const newConfig = clone(config);
      const dayIndex = currentDayTemplateIndex + 1;

      newConfig[dayIndex] = {
        ...newConfig[dayIndex],
        valid: !dayTemplatesErrors?.[currentDayTemplateIndex],
      };

      return newConfig;
    });
  }, [currentDayTemplateIndex, dayTemplatesErrors]);

  useEffect(() => {
    setDaysConfig((config) => {
      const newConfig = clone(config);
      const dayIndex = currentDayTemplateIndex + 1;

      newConfig[dayIndex] = {
        ...newConfig[dayIndex],
        empty: isDayTemplateEmpty(dayTemplates[currentDayTemplateIndex]),
      };

      if (!valuesInitialized && dayTemplates.length === 14) {
        // when proper data get loaded for the first time, evaluate validity of all days values
        const errors = validateDayTemplates(dayTemplates);

        Object.keys(newConfig).forEach((dayConfigIndex, index) => {
          newConfig[dayConfigIndex].valid =
            Object.values(errors?.[index] ?? {}).filter((error) => error !== undefined).length === 0;
        });

        setValuesInitialized(true);
      }

      return newConfig;
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dayTemplates]);

  const daysOfWeek = eachDayOfInterval({
    start: startOfWeek(new Date(), { locale: localeObj }),
    end: endOfWeek(new Date(), { locale: localeObj }),
  });

  const currentDaysConfig = daysConfig[currentDayTemplateIndex + 1];

  useEffect(() => {
    if (!isLoaded) {
      // on first load, when changing from different shift template type, we need to prevent highlighting wrong active day
      setLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDayTemplateIndex]);

  const toggleSelectedForDuplication = (dayIndex: number) => {
    setDaysConfig((config) => {
      const newConfig = clone(config);
      const selectedDayConfig = newConfig[dayIndex];

      if (selectedDayConfig) {
        newConfig[dayIndex] = {
          ...selectedDayConfig,
          selectedForDuplication: !selectedDayConfig.selectedForDuplication,
        };
      }

      return newConfig;
    });
  };

  const resetAllDaysDuplicationState = () => {
    setDaysConfig((config) => {
      const newConfig = clone(config);

      Object.keys(config).forEach((configItem) => {
        newConfig[configItem].selectedForDuplication = false;
      });

      return newConfig;
    });
  };

  const handleCancelDuplication = () => {
    resetAllDaysDuplicationState();
    setDuplicationActive(false);
  };

  const handleSubmitDuplication = () => {
    const daysToDuplicate = Object.keys(daysConfig).filter((dayConfig) => daysConfig[dayConfig].selectedForDuplication);

    if (daysToDuplicate.length) {
      const newDayTemplates = clone(form.getState().values.dayTemplates);
      const newDaysConfig = clone(daysConfig);
      const duplicatedDayTemplate = newDayTemplates[currentDayTemplateIndex];
      const duplicatedDayConfig = newDaysConfig[currentDayTemplateIndex + 1];

      daysToDuplicate.forEach((dayToDuplicate) => {
        const dayToDuplicateIndex = Number.parseInt(dayToDuplicate, 10) - 1;

        newDayTemplates[dayToDuplicateIndex] = duplicateTemplateSettings(
          newDayTemplates[dayToDuplicateIndex],
          duplicatedDayTemplate
        );

        newDaysConfig[dayToDuplicateIndex + 1] = {
          ...duplicatedDayConfig,
        };
      });

      form.change('dayTemplates', newDayTemplates);
      setDaysConfig(newDaysConfig);

      handleCancelDuplication();
    } else {
      setDuplicationActive(false);
    }
  };

  const handleClearCurrentDayTemplate = () => {
    const updatedDayTemplate = {
      ...getInitialValues(dayTemplates[currentDayTemplateIndex].dayIndex).dayTemplates[0],
      id: dayTemplates[currentDayTemplateIndex].id,
    };

    form.mutators.update('dayTemplates', currentDayTemplateIndex, updatedDayTemplate);

    setDaysConfig((config) => {
      const newConfig = clone(config);
      const selectedDayConfig = newConfig[currentDayTemplateIndex + 1];

      if (selectedDayConfig) {
        newConfig[currentDayTemplateIndex + 1] = {
          ...selectedDayConfig,
          empty: true,
          valid: false,
          selectedForDuplication: false,
        };
      }

      return newConfig;
    });
  };

  if (!isLoaded) {
    return null;
  }

  return (
    <Stack className="w-full" direction="vertical" spacing={20} testId={SHIFT_EVEN_WEEK_SWITCHER}>
      {weeksConfig.map(({ name, offset }) => (
        <Stack key={name} className="w-full" direction="vertical">
          <Text color="grey-600" weight="bold">
            {t(name)}
          </Text>
          <Flex justifyContent="between">
            {daysOfWeek.map((dayOfWeek) => {
              const dayIndex = dayOfWeek.getDay();
              // Calculation of even week days for offset=0, odd week days for offset=7
              const dayConfigIndex = (dayIndex || 7) + offset;
              const isDayActive = dayConfigIndex - 1 === currentDayTemplateIndex;

              const handleOnClick = () => {
                if (isDuplicationActive) {
                  if (!isDayActive) {
                    toggleSelectedForDuplication(dayConfigIndex);
                  }
                } else if (!isDayActive) {
                  changeCurrentDayTemplateIndex(dayConfigIndex - 1);
                }
              };

              return (
                <WeekDayTile
                  active={isDayActive}
                  dayOfWeek={dayOfWeek}
                  dataTestId={`${SHIFT_EVEN_ODD_WEEK_DAY_BUTTON}-${dayConfigIndex}`}
                  isDuplicationActive={isDuplicationActive}
                  key={dayOfWeek.getTime()}
                  onClick={handleOnClick}
                  selected={daysConfig[dayConfigIndex].selectedForDuplication}
                  valid={daysConfig[dayConfigIndex].valid && !daysConfig[dayConfigIndex].empty}
                />
              );
            })}
          </Flex>
        </Stack>
      ))}

      {isDuplicationActive && (
        <Text as="div" align="center" color="grey-600">
          {t('shiftTemplate.duplicateDaySettingsDescription')}
        </Text>
      )}

      <Flex justifyContent="between">
        {isDuplicationActive ? (
          <>
            <Button
              onClick={handleCancelDuplication}
              testId={SHIFT_EVEN_ODD_WEEK_CANCEL_SELECTION_BUTTON}
              variant="outline"
            >
              {t('form.cancel')}
              <CrossIcon />
            </Button>
            <Button
              className="mis-auto"
              onClick={handleSubmitDuplication}
              testId={SHIFT_EVEN_ODD_WEEK_CONFIRM_SELECTION_BUTTON}
            >
              <CheckmarkIcon />
              {t('form.confirmSelection')}
            </Button>
          </>
        ) : (
          <>
            {!currentDaysConfig.empty && (
              <Button
                color="error"
                onClick={handleClearCurrentDayTemplate}
                testId={SHIFT_EVEN_ODD_WEEK_CLEAR_BUTTON}
                variant="outline"
              >
                {t('form.clear')}
                <CrossIcon />
              </Button>
            )}
            <Button
              className="mis-auto"
              onClick={() => setDuplicationActive(!isDuplicationActive)}
              testId={SHIFT_EVEN_ODD_WEEK_DUPLICATE_BUTTON}
            >
              <DuplicateIcon />
              {t('shiftTemplate.duplicateDaySettings')}
            </Button>
          </>
        )}
      </Flex>
    </Stack>
  );
};
